import React, { useState } from 'react';
import {
  Timer, Target, ChartBar, ShieldCheck, CheckCircle, X,
  CaretDown, CaretUp, Sparkle
} from '@phosphor-icons/react';
import { trackEvent } from '../../lib/analytics';

/**
 * Shared section components for the Enterprise AI Upskilling pages.
 *
 * Presentation only — every page supplies its own domain-authored copy via
 * src/config/trainingContent.js. Nothing in this file hardcodes a product
 * name, a price, or a claim about outcomes.
 */

export const container = { maxWidth: '1100px', margin: '0 auto', padding: '0 24px' };
export const sectionLight = { padding: '80px 0', background: '#f8fafc' };
export const sectionWhite = { padding: '80px 0', background: 'white' };
export const sectionDark = { padding: '80px 0', background: '#0f172a' };

const h2Light = { fontSize: '2.5rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '16px', lineHeight: 1.15 };
const h2Dark = { ...h2Light, color: 'white' };
const leadLight = { color: '#475569', fontSize: '1.15rem', lineHeight: 1.7, maxWidth: '780px' };
const leadDark = { color: '#cbd5e1', fontSize: '1.15rem', lineHeight: 1.7, maxWidth: '780px' };

/* --------------------------------------------------------------------- */
/* ROI calculator — licence cost is an input, not a hardcoded assumption. */
/* --------------------------------------------------------------------- */

export function ROICalculator({ productName, defaultSeatCost = 360, page }) {
  const [employees, setEmployees] = useState(25);
  const [seatCost, setSeatCost] = useState(defaultSeatCost);
  const [hourlyRate, setHourlyRate] = useState(40);

  const totalLicenseCost = employees * seatCost;
  const underusedLicenseCost = Math.round(totalLicenseCost * 0.85);
  const annualSavingsPerEmp = 2 * 48 * hourlyRate; // 2 hours per week, 48 working weeks
  const totalSavings = Math.round(employees * 0.5 * annualSavingsPerEmp); // 50% adoption

  const onChange = (setter, field) => (e) => {
    const val = Number(e.target.value);
    setter(val);
    trackEvent('roi_calculator_interacted', { field, value: val, page });
  };

  const labelStyle = { display: 'block', fontWeight: 700, marginBottom: '8px', color: '#475569' };
  const valueStyle = { fontWeight: 800, fontSize: '1.2rem', color: '#0071e3', marginBottom: '22px' };

  return (
    <div style={{ background: 'white', borderRadius: '24px', padding: '40px', boxShadow: '0 20px 40px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', maxWidth: '820px', margin: '60px auto 20px' }}>
      <h3 style={{ fontSize: '1.8rem', fontWeight: 800, marginBottom: '10px', textAlign: 'center', color: '#0f172a' }}>
        Justify the Spend: ROI Calculator
      </h3>
      <p style={{ textAlign: 'center', color: '#64748b', fontSize: '0.95rem', lineHeight: 1.6, marginBottom: '28px', maxWidth: '560px', marginLeft: 'auto', marginRight: 'auto' }}>
        Enter your own numbers. Every figure below is your input or a stated assumption — nothing is taken from a vendor deck.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '32px' }}>
        <div>
          <label style={labelStyle}>Number of {productName} licences</label>
          <input type="range" min="5" max="500" step="5" value={employees} onChange={onChange(setEmployees, 'licences')} style={{ width: '100%', marginBottom: '10px' }} />
          <div style={valueStyle}>{employees} licences</div>

          <label style={labelStyle}>Your annual cost per seat ($)</label>
          <input type="range" min="0" max="1000" step="20" value={seatCost} onChange={onChange(setSeatCost, 'seat_cost')} style={{ width: '100%', marginBottom: '10px' }} />
          <div style={valueStyle}>${seatCost}/seat/yr</div>

          <label style={labelStyle}>Average loaded hourly rate ($)</label>
          <input type="range" min="20" max="200" step="5" value={hourlyRate} onChange={onChange(setHourlyRate, 'hourly_rate')} style={{ width: '100%', marginBottom: '10px' }} />
          <div style={{ ...valueStyle, marginBottom: 0 }}>${hourlyRate}/hr</div>
        </div>

        <div style={{ background: '#f8fafc', padding: '24px', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Annual licence spend</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#0f172a' }}>${totalLicenseCost.toLocaleString()} / yr</div>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>At 15% real usage</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#e11d48' }}>${underusedLicenseCost.toLocaleString()} underused</div>
          </div>
          <div style={{ height: '1px', background: '#cbd5e1', margin: '16px 0' }} />
          <div>
            <div style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>If trained: projected time value</div>
            <div style={{ fontSize: '2rem', fontWeight: 900, color: '#10b981' }}>${totalSavings.toLocaleString()} / yr</div>
            <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '10px', lineHeight: 1.55 }}>
              Assumes 2 hours saved per person per week across 48 weeks, at 50% adoption. A projection based on your inputs, not a guaranteed result.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------- */
/* 30% Learn / 70% Do — capability pills              */
/* ------------------------------------------------- */

export function LearnDoSection({ heading, subheading, pills }) {
  return (
    <section style={sectionDark}>
      <div style={container}>
        <div style={{ textAlign: 'center', maxWidth: '760px', margin: '0 auto 44px' }}>
          <span style={{ color: '#38bdf8', fontWeight: 800, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.15em', display: 'block', marginBottom: '14px' }}>
            30% Learn. 70% Do.
          </span>
          <h2 style={h2Dark}>{heading}</h2>
          <p style={{ ...leadDark, margin: '0 auto' }}>{subheading}</p>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', justifyContent: 'center' }}>
          {pills.map((p) => (
            <span key={p} style={{ background: 'rgba(56, 189, 248, 0.12)', border: '1px solid rgba(56, 189, 248, 0.4)', color: '#e0f2fe', padding: '12px 22px', borderRadius: '999px', fontWeight: 700, fontSize: '1rem' }}>
              {p}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------- */
/* Before / After — what changes the next morning     */
/* ------------------------------------------------- */

export function BeforeAfterSection({ heading, subheading, rows }) {
  return (
    <section style={sectionWhite}>
      <div style={container}>
        <style>{`
          @media (max-width: 768px) {
            .ba-row { grid-template-columns: 1fr !important; }
            .ba-arrow { transform: rotate(90deg); margin: 6px 0 !important; }
          }
        `}</style>
        <h2 style={h2Light}>{heading}</h2>
        <p style={{ ...leadLight, marginBottom: '40px' }}>{subheading}</p>

        {rows.map((r, i) => (
          <div
            key={r.before}
            className="ba-row"
            style={{
              display: 'grid', gridTemplateColumns: '1fr 56px 1fr', alignItems: 'center',
              gap: '16px', padding: '22px 24px', borderRadius: '16px',
              background: i % 2 ? '#f8fafc' : 'white', border: '1px solid #e2e8f0', marginBottom: '12px'
            }}
          >
            <div style={{ color: '#64748b', fontSize: '1.02rem', lineHeight: 1.6 }}>{r.before}</div>
            <div className="ba-arrow" style={{ textAlign: 'center', color: '#0071e3', fontWeight: 900, fontSize: '1.3rem' }}>→</div>
            <div style={{ color: '#0f172a', fontSize: '1.02rem', fontWeight: 600, lineHeight: 1.6 }}>{r.after}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ------------------------------------------------- */
/* Hour-by-hour agenda                                */
/* ------------------------------------------------- */

export function AgendaSection({ heading, subheading, items }) {
  return (
    <section style={sectionDark}>
      <div style={{ ...container, maxWidth: '900px' }}>
        <h2 style={h2Dark}>{heading}</h2>
        <p style={{ ...leadDark, marginBottom: '40px' }}>{subheading}</p>
        {items.map((a) => (
          <div key={a.time} style={{ display: 'flex', gap: '28px', padding: '26px 0', borderTop: '1px solid rgba(148, 163, 184, 0.25)' }}>
            <div style={{ flexShrink: 0, width: '76px', fontSize: '1.05rem', fontWeight: 900, color: '#38bdf8', fontVariantNumeric: 'tabular-nums' }}>{a.time}</div>
            <div>
              <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: 'white', margin: '0 0 8px' }}>{a.title}</h3>
              <p style={{ color: '#cbd5e1', fontSize: '1.02rem', lineHeight: 1.7, margin: 0 }}>{a.body}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ------------------------------------------------- */
/* What your team leaves with                         */
/* ------------------------------------------------- */

const deliverableIcons = [
  <Target size={28} weight="duotone" color="#0071e3" />,
  <ChartBar size={28} weight="duotone" color="#0071e3" />,
  <Timer size={28} weight="duotone" color="#0071e3" />,
  <Sparkle size={28} weight="duotone" color="#0071e3" />,
  <ShieldCheck size={28} weight="duotone" color="#0071e3" />,
  <CheckCircle size={28} weight="duotone" color="#0071e3" />
];

export function DeliverablesSection({ heading, subheading, items }) {
  return (
    <section style={sectionLight}>
      <div style={container}>
        <h2 style={h2Light}>{heading}</h2>
        <p style={{ ...leadLight, marginBottom: '40px' }}>{subheading}</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
          {items.map((d, i) => (
            <div key={d.title} style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '30px', boxShadow: '0 10px 26px rgba(15, 23, 42, 0.05)' }}>
              <div style={{ marginBottom: '14px' }}>{deliverableIcons[i % deliverableIcons.length]}</div>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#0f172a', margin: '0 0 10px', lineHeight: 1.4 }}>{d.title}</h3>
              <p style={{ color: '#475569', fontSize: '1rem', lineHeight: 1.65, margin: 0 }}>{d.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------- */
/* Qualification filter                               */
/* ------------------------------------------------- */

export function QualificationSection({ heading, subheading, isFor, isNotFor }) {
  return (
    <section style={sectionWhite}>
      <div style={{ ...container, maxWidth: '1000px' }}>
        <h2 style={h2Light}>{heading}</h2>
        <p style={{ ...leadLight, marginBottom: '40px' }}>{subheading}</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '24px' }}>
          <div style={{ border: '2px solid #10b981', borderRadius: '20px', padding: '30px', background: '#f0fdf4' }}>
            <h3 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#065f46', margin: '0 0 20px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>This is for you if</h3>
            {isFor.map((t) => (
              <div key={t} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', marginBottom: '14px' }}>
                <CheckCircle size={20} weight="fill" color="#10b981" style={{ flexShrink: 0, marginTop: '2px' }} />
                <span style={{ color: '#134e4a', fontSize: '1rem', lineHeight: 1.55 }}>{t}</span>
              </div>
            ))}
          </div>
          <div style={{ border: '1px solid #e2e8f0', borderRadius: '20px', padding: '30px', background: '#f8fafc' }}>
            <h3 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#64748b', margin: '0 0 20px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>This is not for you if</h3>
            {isNotFor.map((t) => (
              <div key={t} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', marginBottom: '14px' }}>
                <X size={20} weight="bold" color="#94a3b8" style={{ flexShrink: 0, marginTop: '3px' }} />
                <span style={{ color: '#64748b', fontSize: '1rem', lineHeight: 1.55 }}>{t}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------- */
/* Outcome guarantee                                  */
/* ------------------------------------------------- */

export function GuaranteeSection({ heading, body }) {
  return (
    <section style={{ padding: '70px 0', background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)' }}>
      <div style={{ ...container, maxWidth: '820px' }}>
        <div style={{ border: '2px dashed #38bdf8', borderRadius: '24px', padding: '40px', textAlign: 'center', background: 'rgba(30, 41, 59, 0.6)' }}>
          <ShieldCheck size={44} weight="duotone" color="#38bdf8" />
          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: 'white', letterSpacing: '-0.02em', margin: '14px 0 16px', lineHeight: 1.25 }}>
            {heading}
          </h2>
          <p style={{ color: '#cbd5e1', fontSize: '1.1rem', lineHeight: 1.7, margin: 0 }}>{body}</p>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------- */
/* Trainer                                            */
/* ------------------------------------------------- */

export function TrainerSection({ heading, intro, page }) {
  return (
    <section style={sectionLight}>
      <div style={{ ...container, maxWidth: '900px' }}>
        <h2 style={{ ...h2Light, marginBottom: '16px' }}>{heading}</h2>
        <p style={{ ...leadLight, marginBottom: '36px' }}>{intro}</p>

        <div style={{ display: 'flex', gap: '36px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <img
            src="/images/abhijit-ghosh.jpg"
            alt="Abhijit Ghosh, Founder of Eagentix and lead trainer"
            style={{ flexShrink: 0, width: '160px', height: '160px', borderRadius: '50%', objectFit: 'cover', border: '3px solid #0071e3', boxShadow: '0 10px 30px rgba(15, 23, 42, 0.15)' }}
          />
          <div style={{ flex: 1, minWidth: '280px' }}>
            <h3 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#0f172a', margin: '0 0 6px' }}>Abhijit Ghosh</h3>
            <p style={{ color: '#0071e3', fontWeight: 700, fontSize: '1rem', margin: '0 0 20px' }}>
              Founder, Eagentix · Data &amp; Generative AI Practitioner · 25 Years · Singapore
            </p>
            {[
              '25 years across Accenture, IBM, Deloitte, Cloudera, Tibco and Wipro — building data platforms and enterprise systems, not selling them.',
              'Delivery experience in banking, insurance, telecom, retail and manufacturing, including regulated environments with real audit and access constraints.',
              'Current focus on generative AI and agentic workflows, and on moving initiatives from proof of concept to production rather than leaving them at pilot.'
            ].map((b) => (
              <div key={b} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', marginBottom: '14px' }}>
                <ShieldCheck size={20} weight="duotone" color="#0071e3" style={{ flexShrink: 0, marginTop: '2px' }} />
                <span style={{ color: '#475569', fontSize: '1.02rem', lineHeight: 1.65 }}>{b}</span>
              </div>
            ))}
            <a
              href="https://www.linkedin.com/in/abhijitghoshsingapore/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent('outbound_click', { destination: 'linkedin', page })}
              style={{ display: 'inline-block', marginTop: '10px', padding: '11px 22px', borderRadius: '10px', border: '1px solid #cbd5e1', color: '#0f172a', fontWeight: 700, textDecoration: 'none', fontSize: '0.95rem' }}
            >
              View LinkedIn profile →
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------- */
/* Confidentiality note (replaces named testimonials) */
/* ------------------------------------------------- */

export function ConfidentialityNote({ text }) {
  return (
    <section style={{ padding: '48px 0', background: '#020617' }}>
      <div style={{ ...container, maxWidth: '820px' }}>
        <div style={{ border: '1px solid rgba(148, 163, 184, 0.3)', borderRadius: '18px', padding: '28px 32px', background: 'rgba(15, 23, 42, 0.85)' }}>
          <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
            <ShieldCheck size={26} weight="duotone" color="#38bdf8" style={{ flexShrink: 0, marginTop: '2px' }} />
            <p style={{ color: '#cbd5e1', fontSize: '1.02rem', lineHeight: 1.7, margin: 0 }}>{text}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------- */
/* FAQ                                                */
/* ------------------------------------------------- */

function FAQItem({ faq, index, page }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: '1px solid #e2e8f0' }}>
      <button
        onClick={() => {
          setOpen(!open);
          if (!open) trackEvent('faq_expanded', { question: faq.question, index, page });
        }}
        style={{ width: '100%', background: 'none', border: 'none', padding: '22px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '20px', cursor: 'pointer', textAlign: 'left' }}
      >
        <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a' }}>{faq.question}</span>
        {open
          ? <CaretUp size={20} weight="bold" color="#0071e3" style={{ flexShrink: 0 }} />
          : <CaretDown size={20} weight="bold" color="#94a3b8" style={{ flexShrink: 0 }} />}
      </button>
      {open && (
        <p style={{ color: '#475569', fontSize: '1.02rem', lineHeight: 1.7, padding: '0 0 24px', margin: 0, maxWidth: '780px' }}>
          {faq.answer}
        </p>
      )}
    </div>
  );
}

export function FAQSection({ heading, faqs, page }) {
  return (
    <section style={sectionWhite}>
      <div style={{ ...container, maxWidth: '860px' }}>
        <h2 style={{ ...h2Light, marginBottom: '28px' }}>{heading}</h2>
        {faqs.map((faq, i) => <FAQItem key={faq.question} faq={faq} index={i} page={page} />)}
      </div>
    </section>
  );
}

/* ------------------------------------------------- */
/* Final CTA wrapper                                  */
/* ------------------------------------------------- */

export function FinalCTASection({ eyebrow, heading, subheading, note, children, ps }) {
  return (
    <section id="lead-form" style={{ padding: '80px 0', background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)' }}>
      <div style={{ ...container, maxWidth: '720px' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <span style={{ color: '#38bdf8', fontWeight: 800, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.15em', display: 'block', marginBottom: '12px' }}>
            {eyebrow}
          </span>
          <h2 style={{ fontSize: '2.4rem', fontWeight: 800, color: 'white', letterSpacing: '-0.03em', marginBottom: '16px', lineHeight: 1.2 }}>{heading}</h2>
          <p style={{ color: '#e2e8f0', fontSize: '1.2rem', fontWeight: 600, lineHeight: 1.55, marginBottom: '14px' }}>{subheading}</p>
          {note && <p style={{ color: '#94a3b8', fontSize: '0.98rem', lineHeight: 1.65 }}>{note}</p>}
        </div>

        {children}

        {ps && (
          <div style={{ marginTop: '40px', padding: '24px', borderRadius: '16px', background: 'rgba(30, 41, 59, 0.8)', border: '1px dashed #38bdf8', textAlign: 'center' }}>
            <p style={{ color: '#94a3b8', fontSize: '0.95rem', lineHeight: 1.6, margin: 0, fontStyle: 'italic' }}>
              <strong style={{ color: '#38bdf8', fontStyle: 'normal' }}>P.S.</strong> {ps}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

export { trackEvent };
